import { AppointmentESJsonType } from 'api/AppointmentController/getAppointmentsForCoop';
import { deletePastTour } from 'api/TourController';
import { Text } from 'components/Typography/Text';
import { TableColumnType, Title, Button, Icon } from 'components';
import { isAfter, isBefore } from 'date-fns/esm';
import { Link } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AppointmentStatus, Timezone } from '@bb/domain/dist/types';
import { createFormatDateFunction, getLocalTimeZone } from 'utils';
import { AuthContext } from 'contexts';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  buildItemCountText,
  getStartOfDayInTimezone,
} from '../../TourEditPage/utils';
import { StyledBox } from '../../AppointmentDeskPage/StyledComponents';
import { BuyerTableData, CommonInnerTableProps } from '../types';
import BuyerTable from './BuyerTable';
import { StyledInnerTable } from './components';
import { TIME_CHANGE_STATUSES, ES_DELAY } from '../constants';

type ToursMap = Record<
  string, // appointmentTourId
  Record<
    string, // id
    AppointmentESJsonType & {
      passed?: boolean;
    }
  > & { tourStartTime?: Date | string; tourEndTime?: Date | string }
>;

// helper function
const createSeparatedShowingsAndTours = (showings: BuyerTableData) => {
  const separatedShowingsAndTours: BuyerTableData[] = [];
  const toursMap: ToursMap = {};

  showings.forEach(
    (
      {
        appointmentTourId,
        id,
        isFirstItem,
        appointmentTourItemId,
        docType,
        tourName,
      },
      i,
    ) => {
      // if appointment is not part of a tour
      if (typeof tourName !== 'string') {
        if (
          separatedShowingsAndTours[separatedShowingsAndTours.length - 1]?.[0]
            ?.tourName ||
          !separatedShowingsAndTours[separatedShowingsAndTours.length - 1]
        ) {
          separatedShowingsAndTours.push([showings[i]]);
        } else {
          separatedShowingsAndTours[separatedShowingsAndTours.length - 1].push(
            showings[i],
          );
        }
        // if appointment is part of a tour
      } else {
        if (!toursMap[appointmentTourId]) {
          toursMap[appointmentTourId] = {};
        }

        // for non draft items appointmentTourItemId should be used as the key of the item in toursMap
        // and will be used later to order the tour.
        // this is because the nextItem field is a refrence of AppointmentTourItem collection's id
        if (docType === 'tourItem') {
          toursMap[appointmentTourId][id] = showings[i];
        } else {
          toursMap[appointmentTourId][appointmentTourItemId] = showings[i];
        }

        // we will only add the first item of the tour here, rest will be added in the forEach below using toursMap
        if (isFirstItem) {
          separatedShowingsAndTours.push([showings[i]]);
        }
      }
    },
  );

  // populating tours with rest of the tour items using toursMap
  separatedShowingsAndTours.forEach(showings_ => {
    if (typeof showings_[0].tourName === 'string') {
      let currentNode = showings_[0].nextItem;
      const { appointmentTourId } = showings_[0];

      // showings_[0].isFirstItem should always be true, just in case.
      if (showings_[0].isFirstItem && toursMap[appointmentTourId]) {
        toursMap[appointmentTourId].tourStartTime =
          TIME_CHANGE_STATUSES.includes(
            showings_[0].status as AppointmentStatus,
          )
            ? showings_[0].originalTime
            : showings_[0].time;
        toursMap[appointmentTourId].tourEndTime = TIME_CHANGE_STATUSES.includes(
          showings_[0].status as AppointmentStatus,
        )
          ? showings_[0].originalTimeEnd
          : showings_[0].timeEnd;
      }

      while (currentNode) {
        const currentTourItem = toursMap[appointmentTourId][currentNode];
        if (!currentTourItem) break;
        // finding startTime and endTime for the tour based on tour items times
        if (currentTourItem.time) {
          let currentStartTime = toursMap[appointmentTourId].tourStartTime;
          let currentEndTime = toursMap[appointmentTourId].tourEndTime;
          const tourItemStartTime = TIME_CHANGE_STATUSES.includes(
            currentTourItem.status as AppointmentStatus,
          )
            ? currentTourItem.originalTime
            : currentTourItem.time;
          const tourItemEndTime = TIME_CHANGE_STATUSES.includes(
            currentTourItem.status as AppointmentStatus,
          )
            ? currentTourItem.originalTimeEnd
            : currentTourItem.timeEnd;
          if (currentStartTime) {
            currentStartTime = isBefore(
              new Date(tourItemStartTime),
              new Date(currentStartTime),
            )
              ? tourItemStartTime
              : currentStartTime;
          } else {
            currentStartTime = tourItemStartTime;
          }

          if (currentEndTime) {
            currentEndTime = isAfter(
              new Date(tourItemEndTime),
              new Date(currentEndTime),
            )
              ? tourItemEndTime
              : currentEndTime;
          } else {
            currentEndTime = tourItemEndTime;
          }

          toursMap[appointmentTourId].tourStartTime = currentStartTime;
          toursMap[appointmentTourId].tourEndTime = currentEndTime;
        }
        showings_.push(currentTourItem);
        currentNode = currentTourItem.nextItem;
      }
    }
  });
  return { separatedShowingsAndTours, toursMap };
};

const isPastTour = (tourDate: string, timezone: Timezone) => {
  const startOfToday = getStartOfDayInTimezone(new Date(), timezone);
  const tourDateInTimezone = getStartOfDayInTimezone(tourDate, timezone);
  return isBefore(tourDateInTimezone, startOfToday);
};

const formatTime = createFormatDateFunction('h:mmaaa');

const BuyerTableWrapper: React.FC<CommonInnerTableProps<BuyerTableData>> = ({
  tableKey,
  showings,
  setSelectedCovidAppt,
}) => {
  const { auth } = useContext(AuthContext);
  const { refetchPage } = useShowingsCalendarContext();
  const [showingsAndTours, setShowingsAndTours] = useState<BuyerTableData[]>(
    [],
  );
  const [toursMap, setToursMap] = useState<ToursMap>({});

  useEffect(() => {
    if (showings) {
      const { separatedShowingsAndTours, toursMap: toursMap_ } =
        createSeparatedShowingsAndTours(showings);
      setShowingsAndTours(separatedShowingsAndTours);
      setToursMap(toursMap_);
    }
  }, [showings]);

  const columns: TableColumnType<{
    showings: BuyerTableData;
    startTime?: Date | string;
    endTime?: Date | string;
  }>[] = useMemo(
    () => [
      {
        key: 'tourName',
        dataIndex: 'showings',
        render: (showings_, { startTime, endTime }) => {
          const itemCount = showings_.length;
          return (
            <StyledBox $flexDirection="column">
              <TourTitle level={5}>{showings_[0].tourName}</TourTitle>
              <Text type="secondary" nowrap>
                {startTime && endTime
                  ? `${formatTime(
                      startTime,
                      getLocalTimeZone(auth),
                    )} - ${formatTime(endTime, getLocalTimeZone(auth))} `
                  : `No times selected yet `}
                • {buildItemCountText(itemCount)}
              </Text>
            </StyledBox>
          );
        },
      },
      {
        key: 'tourEditButton',
        dataIndex: 'showings',
        align: 'right',
        render: showings_ => isPastTour(showings_[0].date, getLocalTimeZone(auth)) ? (
            <Button
              icon={
                <Icon
                  color="black"
                  type="antdDeleteFilled"
                  width={12}
                  offset={[0, -1]}
                />
              }
              onClick={async () => {
                await deletePastTour(showings_[0].appointmentTourId!);
                refetchPage(ES_DELAY.DEFAULT);
              }}
            >
              Remove
            </Button>
          ) : (
            <Link
              to={`/tours/${showings_[0].appointmentTourId}`}
              data-cy="buyer-entry-tour-link"
            >
              <Button
                icon={
                  <Icon type="antdEditFilled" width={12} offset={[0, -3]} />
                }
              >
                Edit
              </Button>
            </Link>
          ),
      },
    ],
    [],
  );

  return (
    <>
      {showingsAndTours.map((showingsArray, i) => {
        if (typeof showingsArray[0].tourName !== 'string') {
          return (
            <BuyerTable
              key={`${showingsArray[0].date}-${i}`}
              tableKey={showingsArray[0].time}
              showings={showingsArray}
              setSelectedCovidAppt={setSelectedCovidAppt}
            />
          );
        }

        const dataSource: {
          showings: BuyerTableData;
          startTime?: Date | string;
          endTime?: Date | string;
        }[] = [
          {
            showings: showingsArray,
            startTime:
              toursMap[showingsArray[0].appointmentTourId]?.tourStartTime,
            endTime: toursMap[showingsArray[0].appointmentTourId]?.tourEndTime,
          },
        ];
        return (
          <StyledInnerTable
            key={`${tableKey}-${i}`}
            columns={columns}
            components={{
              body: {
                row: StyledTableRow,
                cell: StyledTableCell,
              },
            }}
            rowKey={record =>
              `${record.showings[0].appointmentTourId}-tourOuterTable-${i}`
            }
            showHeader={false}
            pagination={false}
            expandIconColumnIndex={-1}
            tableLayout="fixed"
            expandable={{
              defaultExpandAllRows: true,
              expandedRowRender: (record, i_) => (
                <BuyerTable
                  isTourTable
                  key={`${showingsArray[0].date}-tourInnerTable-${i_}`}
                  tableKey={`${record.showings[0].appointmentTourId}-tourInnerTable-${i_}`}
                  showings={record.showings}
                  setSelectedCovidAppt={setSelectedCovidAppt}
                />
              ),
            }}
            dataSource={dataSource}
            data-cy="buyer-table"
          />
        );
      })}
    </>
  );
};

export default BuyerTableWrapper;

const StyledTableCell = styled.td`
  border-bottom: 1px solid #ffffff !important;
`;

const StyledTableRow = styled.tr`
  /* &:nth-child(odd) > td {
    padding-left: 0;
  } */
  &:nth-child(even) > td {
    padding-left: 0;
  }
`;

const TourTitle = styled(Title)`
  font-size: 16px !important;
  margin-bottom: 2px !important;
`;
